import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import ErrorMessage from "../../../form/ErrorMessage";
import Eye from "../../../form/Eye";
import { registerFindAJobSchema } from "../../../../utils/schema";
import { freelancerSignUp } from "../../../../redux/features/find-a-job/actions/authAction";
import "./ApplyNowSignupModal.css";

const ApplyNowSignupModal = ({ onClose, onContinue, onOpenSignIn, jobDetails }) => {
  const dispatch = useDispatch();
  const { subLoading } = useSelector((state) => state?.freelancer?.auth || {});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [apiError, setApiError] = useState("");
  const [isEmailExistsApiError, setIsEmailExistsApiError] = useState(false);
  const emailAlreadyInUseMessage = "The email address is already in use";

  const isEmailExistsError = (message) => {
    const msg = String(message || "").toLowerCase();
    return (
      (msg.includes("email") && msg.includes("exist")) ||
      (msg.includes("email") && msg.includes("already"))
    );
  };

  const handleContinue = (values, setFieldError, setFieldTouched) => {
    setApiError("");
    const fullName = `${String(values.first_name || "").trim()} ${String(
      values.last_name || ""
    ).trim()}`.trim();

    const payload = {
      email: String(values.email || "").trim(),
      password: values.password,
      full_name: fullName,
      talent_type: "JOB_SEEKER",
      type: "GUEST",
    };

    dispatch(
      freelancerSignUp({
        payload,
        callback: (response, error) => {
          const message =
            error?.data?.message ||
            error?.message ||
            response?.message ||
            "Registration failed. Please try again.";

          if (error) {
            if (isEmailExistsError(message)) {
              setIsEmailExistsApiError(true);
              setApiError("");
              setFieldTouched("email", true, false);
              setFieldError("email", emailAlreadyInUseMessage);
            } else {
              setApiError(message);
            }
            return;
          }

          const isSuccess = response?.success === true || response?.status === true;
          if (!isSuccess) {
            if (isEmailExistsError(message)) {
              setIsEmailExistsApiError(true);
              setApiError("");
              setFieldTouched("email", true, false);
              setFieldError("email", emailAlreadyInUseMessage);
            } else {
              setApiError(message);
            }
            return;
          }

          const signupToken =
            response?.data?.token ||
            response?.token ||
            response?.access_token ||
            response?.data?.access_token ||
            response?.data?.auth_token ||
            "";

          if (onContinue) {
            onContinue({
              ...values,
              full_name: payload.full_name,
              email: payload.email,
              auth_token: signupToken,
              job_id: jobDetails?.id || "",
              job_title: jobDetails?.job_title || "",
              company_name: jobDetails?.company_name || "",
            });
          }
        },
      })
    );
  };

  const handleOverlayClick = (event) => {
    event.stopPropagation();
    if (event.target === event.currentTarget) {
      onClose?.();
    }
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="ct_signup_modal_overlay"
      role="dialog"
      aria-modal="true"
      onClick={handleOverlayClick}
    >
      <div
        className="ct_signup_modal_container ct_signup_modal_container_new bg-white"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="ct_signup_modal_close"
          onClick={onClose}
          aria-label="Close signup modal"
        >
          <i className="fa-solid fa-xmark"></i>
        </button>

        <div className="ct_signup_modal_left ct_signup_modal_left_space_new">
          <img
            src={
              isEmailExistsApiError
                ? "assets/img/Rectangle 4179_new.png"
                : "assets/img/Rectangle 4179 (1).png"
            }
            alt="Signup visual"
          />
        </div>

        <div className="ct_signup_modal_right ct_signup_modal_right_space_new">
          <div className="ct_signup_modal_logo_wrap">
            <img src="assets/img/Nuvio_logo.png" alt="Nuvio" />
          </div>

          <h3 className="ct_signup_modal_title">
            Hi! Find job with <span>Nuvio</span>
          </h3>

          <Formik
            initialValues={{
              first_name: "",
              last_name: "",
              email: "",
              password: "",
              confirm_password: "",
            }}
            validationSchema={registerFindAJobSchema}
            onSubmit={(values, { setFieldError, setFieldTouched }) => {
              handleContinue(values, setFieldError, setFieldTouched);
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
            }) => (
              <form className="ct_signup_modal_form" onSubmit={handleSubmit}>
                <div className="ct_signup_modal_row">
                  <div className="ct_signup_modal_group">
                    <label>First Name</label>
                    <input
                      type="text"
                      id="first_name"
                      name="first_name"
                      placeholder="Enter first name"
                      value={values.first_name}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                    <ErrorMessage
                      errors={errors}
                      touched={touched}
                      fieldName="first_name"
                    />
                  </div>
                  <div className="ct_signup_modal_group">
                    <label>Last Name</label>
                    <input
                      type="text"
                      id="last_name"
                      name="last_name"
                      placeholder="Enter last name"
                      value={values.last_name}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                    <ErrorMessage
                      errors={errors}
                      touched={touched}
                      fieldName="last_name"
                    />
                  </div>
                </div>
                <div className="ct_signup_modal_group">
                  <label>Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Enter Email"
                    value={values.email}
                    onBlur={handleBlur}
                    onChange={(event) => {
                      setIsEmailExistsApiError(false);
                      setApiError("");
                      handleChange(event);
                    }}
                  />
                  <ErrorMessage
                    errors={errors}
                    touched={touched}
                    fieldName="email"
                  />
                </div>
                <div className="ct_signup_modal_group ct_signup_modal_password_group">
                  <label>Password</label>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    placeholder="*******"
                    value={values.password}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                  <div className="ct_signup_modal_eye">
                    <Eye isEye={showPassword} onClick={setShowPassword} />
                  </div>
                  <ErrorMessage
                    errors={errors}
                    touched={touched}
                    fieldName="password"
                  />
                </div>

                <div className="ct_signup_modal_group ct_signup_modal_password_group">
                  <label>Confirm Password</label>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirm_password"
                    name="confirm_password"
                    placeholder="*******"
                    value={values.confirm_password}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                  <div className="ct_signup_modal_eye">
                    <Eye isEye={showConfirmPassword} onClick={setShowConfirmPassword} />
                  </div>
                  <ErrorMessage
                    errors={errors}
                    touched={touched}
                    fieldName="confirm_password"
                  />
                </div>

                {apiError ? <p className="ct_modal_input_error mt-1">{apiError}</p> : null}

                <button
                  type={isEmailExistsApiError ? "button" : "submit"}
                  className={`ct_signup_modal_continue ${isEmailExistsApiError ? "ct_signup_modal_continue_profile" : ""}`}
                  onClick={() => {
                    if (isEmailExistsApiError) {
                      if (onOpenSignIn) onOpenSignIn();
                    }
                  }}
                  disabled={subLoading}
                >
                  {subLoading
                    ? "Please wait..."
                    : isEmailExistsApiError
                      ? "Start My Profile"
                      : "Continue"}
                </button>
              </form>
            )}
          </Formik>

          {isEmailExistsApiError ? (
            <p className="ct_signup_modal_hint_text">
              Profiles with on <span>NUVIO</span> are up to 3x more likely to be selected by hirers.
            </p>
          ) : (
            <p className="ct_signup_modal_footer">
              Already have an account?{" "}
              <span
                onClick={() => {
                  if (onOpenSignIn) onOpenSignIn();
                }}
              >
                Sign In
              </span>
            </p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ApplyNowSignupModal;
